/**
 * Utilities for managing time periods dynamically
 */

import { Task } from '../types/roadmap';

export interface TimePeriodConfig {
  id: string;
  label: string;
  color: {
    bg: string;
    border: string;
    headerBg: string;
  };
  isHistorical: boolean;
  isFuture: boolean;
  sortOrder: number;
}

// Time period configurations - easily extensible
export const TIME_PERIOD_CONFIGS: Record<string, TimePeriodConfig> = {
  pi5: {
    id: 'pi5',
    label: 'PI5',
    color: {
      bg: 'bg-orange-100',
      border: 'border-orange-500',
      headerBg: 'bg-orange-500 bg-opacity-30',
    },
    isHistorical: true,
    isFuture: false,
    sortOrder: 1,
  },
  pi5_pi6: {
    id: 'pi5_pi6',
    label: 'PI5-PI6',
    color: {
      bg: 'bg-gray-100',
      border: 'border-gray-600',
      headerBg: 'bg-gray-700',
    },
    isHistorical: false,
    isFuture: false,
    sortOrder: 2,
  },
  pi6: {
    id: 'pi6',
    label: 'PI6',
    color: {
      bg: 'bg-blue-100',
      border: 'border-blue-500',
      headerBg: 'bg-blue-500 bg-opacity-30',
    },
    isHistorical: false,
    isFuture: false,
    sortOrder: 3,
  },
  pi7: {
    id: 'pi7',
    label: 'PI7',
    color: {
      bg: 'bg-green-100',
      border: 'border-green-500',
      headerBg: 'bg-green-500 bg-opacity-30',
    },
    isHistorical: false,
    isFuture: true,
    sortOrder: 4,
  },
  pi8: {
    id: 'pi8',
    label: 'PI8',
    color: {
      bg: 'bg-purple-100',
      border: 'border-purple-500',
      headerBg: 'bg-purple-500 bg-opacity-30',
    },
    isHistorical: false,
    isFuture: true,
    sortOrder: 5,
  },
};

/**
 * Get all unique time periods from tasks
 * If no tasks exist, returns current (non-historical, non-future) periods
 */
export function getAvailableTimePeriods(tasks: Task[]): TimePeriodConfig[] {
  const uniquePeriods = new Set(tasks.map(t => t.time_period));

  // If no tasks exist, show default current periods
  if (uniquePeriods.size === 0) {
    return Object.values(TIME_PERIOD_CONFIGS)
      .filter(p => !p.isHistorical && !p.isFuture)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  return Array.from(uniquePeriods)
    .map(periodId => TIME_PERIOD_CONFIGS[periodId])
    .filter(Boolean)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Get time periods to display based on showHistorical and showFuture flags
 */
export function getVisibleTimePeriods(
  tasks: Task[],
  showHistorical: boolean,
  showFuture: boolean = true
): TimePeriodConfig[] {
  const available = getAvailableTimePeriods(tasks);

  return available.filter(p => {
    // Filter out historical periods unless showHistorical is true
    if (p.isHistorical && !showHistorical) {
      return false;
    }

    // Filter out future periods unless showFuture is true
    if (p.isFuture && !showFuture) {
      return false;
    }

    return true;
  });
}

/**
 * Get time period config by ID, with fallback for unknown periods
 */
export function getTimePeriodConfig(periodId: string): TimePeriodConfig {
  return TIME_PERIOD_CONFIGS[periodId] || {
    id: periodId,
    label: periodId.toUpperCase(),
    color: {
      bg: 'bg-gray-100',
      border: 'border-gray-400',
      headerBg: 'bg-gray-400 bg-opacity-30',
    },
    isHistorical: false,
    isFuture: false,
    sortOrder: 999,
  };
}
